/**
 * carriesWeekly — does this advance have a weekly of its own, or is it settled at the end?
 *
 * ITS SCHEDULE, AND NOTHING ELSE [D-B58]. An advance with no schedule row collects nothing
 * separately while the vehicle is being paid for; once the vehicle is paid for, the contract
 * instalment keeps running and credits that advance until it is recovered. Both tabs say so on the
 * card — a neutral badge and a striped fill on the bar — and both need the same answer, from the
 * same reading, or one contract's two tabs could describe one arrangement two ways.
 *
 * IT USED TO READ THE MONEY AS WELL, and the money is about to start moving on exactly the advances
 * this predicate exists for: the first pesewa the engine credits would set `allocated_total` above
 * nought, be read as a schedule, and take the badge and the fill away from the one arrangement they
 * are there to describe.
 *
 * `[0]` IS THE LATEST ROW. `get_contract_insights` builds the array `ORDER BY starts_at DESC`, the
 * same order the allocation engine takes its weekly in, and the `> 0` limb is the engine's own
 * admission test: a latest row of nought is no schedule at all. Neither tier holds a row of that
 * shape today, so this reads the same `false` it reads now until the engine changes.
 *
 * PURE, AND FREE OF THE COMPONENT GRAPH, so vitest can hold it under node — neither tab is rendered
 * by any test in this repo, and this is the sentence both of them turn on.
 */

/**
 * The one field of an advance this question needs — `loan_instalments` or
 * `insurance_loan_instalments` as `get_contract_insights` hands them back.
 *
 * STRUCTURAL, so either tab passes its own row straight in. NULL AND NOT `[]` WHERE THERE ARE NONE:
 * the payload builds the array with `json_agg(...)`, which returns SQL NULL over an empty set, so a
 * null here is a counted zero rather than an unread one.
 */
export interface WeeklyBearingAdvance {
    instalments: readonly { amount: number }[] | null;
}

/** True where the advance has a schedule of its own to be collected on. */
export function carriesWeekly(loan: WeeklyBearingAdvance | null | undefined): boolean {
    return (loan?.instalments?.[0]?.amount ?? 0) > 0;
}
